import LoadingButton from './LoadingButton';
import { useEffect, useState } from 'react';
import {
  AddToCartButtonProps,
  CartDetailType,
  GuestCartResponse,
  OutOfStock,
  Product,
} from '../interface/interface';
import { calculateOutOfStockEnum } from '../utils/outOfStockStatus';
import showToast from './ToastComponent';
import ProductQuantityIncrement from './ProductQuantityIncrement';
import { AllowedProducts } from './AllowedProducts';
import CustomModal from './CustomModal';
import ProductCards from './ProductCards';
import { useNavigate } from 'react-router-dom';
import addGuestProductCart from '../service/addGuestProductCart';
import getGuestCart from '../service/getGuestCart';
import removeGuestProductFromCart from '../service/removeGuestProductFromCart';
import Loading from './Loading';
import { useGuestCart } from '../context/guestCartContext';
import isMobile from 'ismobilejs';
import FloatingCartMobile from './FloatingCartMobile';
import FloatingCart from './FloatingCart';

const GuestAddToCartButton = ({
  product,
  substituteProduct,
}: AddToCartButtonProps) => {
  const [cart, setCart] = useState<CartDetailType | null>(null);
  const [products, setProducts] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [openSubstituteModal, setSubstituteModal] = useState(false);
  const [suggestAccessoriesModal, setSuggestAccessories] = useState(false);
  const [addProductModalLoading, setAddProductModalLoading] = useState(false);
  const { refreshCart } = useGuestCart();

  const navigate = useNavigate();

  async function addProductToCart(id: string) {
    try {
      setLoading(true);

      const result = await addGuestProductCart(id, 1);

      if (result?.success) {
        showToast({
          message: 'Item added to your cart',
          type: 'success',
        });

        const cartResponse: GuestCartResponse = await getGuestCart();

        if (!cartResponse?.cart) {
          setCart(null);
          setLoading(false);
          return;
        }

        setCart(cartResponse?.cart);

        setLoading(false);
      } else {
        showToast({
          message: result?.message || 'Failed to add item',
          type: 'warning',
          timeout: 6000,
        });
        setLoading(false);
      }
      await refreshCart();
    } catch (error) {
      console.error(error);
      showToast({
        message: 'Failed to add item',
        type: 'error',
      });
      setLoading(false);
    }
  }

  async function removeProductFromCart(id: string) {
    try {
      setLoading(true);
      const result = await removeGuestProductFromCart(id);

      if (result?.success) {
        showToast({
          message: 'Item removed from your cart.',
          type: 'success',
        });

        const guestcCart = await getGuestCart();

        if (!guestcCart?.cart) {
          setCart(null);
          setLoading(false);
          return;
        }

        setCart(guestcCart.cart);
        setLoading(false);
      } else {
        showToast({
          message: result?.message || 'Unable to remove item',
          type: 'warning',
        });
        setLoading(false);
      }
      await refreshCart();
    } catch (error) {
      console.error(error);
      showToast({
        message: 'Unable to remove item',
        type: 'error',
      });
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!cart) {
      getGuestCart().then((res) => setCart(res?.cart!));
    }
    setProducts(frameProductObject(product));
  }, [cart, product?.product_code]);

  function frameProductObject(product: Product) {
    const orderedQuantity =
      cart?.line_items?.find(
        (item: any) => `${item?.product_id}` === `${product.product_code}`,
      )?.quantity ?? 0;

    if (substituteProduct) {
      const subProduct = substituteProduct;
      const subOrderedQuantity =
        cart?.line_items?.find(
          (item: any) => `${item?.product_id}` === `${subProduct.product_code}`,
        )?.quantity ?? 0;

      return {
        ...product,
        substituteProduct: {
          ...subProduct,
          orderedQuantity: subOrderedQuantity,
          outOfStock: calculateOutOfStockEnum(subProduct, orderedQuantity),
        },
        orderedQuantity,
        outOfStock: calculateOutOfStockEnum(product, orderedQuantity),
      };
    }

    return {
      ...product,
      orderedQuantity,
      outOfStock: calculateOutOfStockEnum(product, orderedQuantity),
    };
  }

  async function handleAddToCartClick() {
    setLoading(true);
    await addProductToCart(product.product_code).finally(() =>
      setLoading(false),
    );
  }

  async function handleProceed() {
    setAddProductModalLoading(true);
    await addProductToCart(product.product_code).then(() => {
      setAddProductModalLoading(false);
      navigate('/non-script-range');
    });
  }

  async function handleStayOnPage() {
    setSuggestAccessories(false);
    setLoading(true);
    await addProductToCart(product.product_code).finally(() =>
      setLoading(false),
    );
  }

  if (loading) return <Loading />;

  return (
    <div>
      {products?.orderedQuantity > 0 ? (
        <ProductQuantityIncrement
          productCode={product.product_code}
          orderedQuantity={products.orderedQuantity}
          productOutOfStock={products.outOfStock}
          addProductToCart={async (id: string) => {
            await addProductToCart(id);
          }}
          removeProductFromCart={async (productId: string) => {
            await removeProductFromCart(productId);
          }}
        />
      ) : (
        <>
          <LoadingButton
            buttonText="Add to Cart"
            isLoading={loading}
            buttonIcon={<span>🛒</span>}
            isDisabled={
              products?.outOfStock && products.outOfStock !== OutOfStock.false
            }
            onClick={handleAddToCartClick}
          />
          {products?.allowedProduct && products?.allowedProducts && (
            <AllowedProducts
              product={products}
              addProductToCart={async (id: string) => {
                await addGuestProductCart(id);
              }}
              removeProductFromCart={async (productId: string) => {
                await removeGuestProductFromCart(productId);
              }}
            />
          )}
        </>
      )}
      {substituteProduct && !product.substitutions_not_available && (
        <div className="">
          <button
            className="w-11/14 bg-secondary-cornflowerblue-600 border border-secondary-cornflowerblue-600 py-3 px-2
              rounded-md text-white text-sm font-semibold 
              hover:text-secondary-cornflowerblue-600 hover:bg-white"
            onClick={() => setSubstituteModal(true)}
          >
            Order substitution
          </button>
          <CustomModal
            isOpen={openSubstituteModal}
            onClose={() => setSubstituteModal(false)}
            header=""
            footer=""
            modalSize="w-11/12 sm-land:w-3/4 md:w-3/5 lg:w-2/5 2xl:w-1/4 3xl:w-1/5"
          >
            <div>
              <p className="mb-3 text-sm font-regular text-primary-blue-500 text-left">
                Substitute product for:{' '}
                <span className="font-semibold">{product.name}</span>
              </p>
              <ProductCards
                product={products?.substituteProduct}
                addProductToCart={async (id: string) => {
                  await addGuestProductCart(id);
                }}
                removeProductFromCart={async (productId: string) => {
                  await removeGuestProductFromCart(productId);
                }}
                substitueCard={true}
                isAccessories={false}
                allowedProduct={false}
              />
            </div>
          </CustomModal>
        </div>
      )}
      <div className="absolute text-start bottom-4 left-0 w-full z-40">
        <CustomModal
          isOpen={suggestAccessoriesModal}
          onClose={handleStayOnPage}
          header="Dried Herb Product Added!"
          footer={
            <>
              <LoadingButton
                isLoading={addProductModalLoading}
                className="!text-base !px-6 !py-3 !w-auto !rounded-md !mr-2"
                onClick={handleProceed}
                buttonText="Proceed"
              />
              <button
                className="text-base px-6 py-3 rounded-md !border !border-primary-blue-500"
                onClick={handleStayOnPage}
              >
                Stay on this page
              </button>
            </>
          }
          modalSize="w-11/12 sm-land:w-3/4 md:w-3/5 lg:w-2/5 2xl:w-1/4"
        >
          <div className="text-start">
            Would you like to purchase required accessories along with this?
          </div>
        </CustomModal>
      </div>
      {!isMobile().phone ? <FloatingCart /> : <FloatingCartMobile />}
    </div>
  );
};

export default GuestAddToCartButton;
